import { useCallback, useEffect, useState } from "react";
import { Flex, Icon, Icons, Spin, Switch, useToast } from "@ir/ui";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { Permissions } from "@src/domain/permissions";
import { UpdateBrandUserStatusDTO } from "@src/domain/dto/brands";
import { UpdateBrandUserStatusUseCase } from "@src/application/useCases/brand";
import { BrandRepository } from "@src/infrastructure/repositories/brand";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import { useUsersAccessData } from "@src/presentation/providers/UsersAccessDataProvider";
import type { UserDetailsBrand } from "@src/domain/entities/auth/user/UserDetails";

export interface UserBrandAccessStatusUpdateProps {
  brand: UserDetailsBrand;
}

export const UserBrandAccessStatusUpdate = ({
  brand,
}: Readonly<UserBrandAccessStatusUpdateProps>) => {
  const { applyUserBrandAccessUpdate } = useUsersAccessData();
  const [pendingActive, setPendingActive] = useState(brand.isAccessActive);
  const [isUpdating, setIsUpdating] = useState(false);
  const { error: errorToast, success: successToast } = useToast();

  useEffect(() => {
    setPendingActive(brand.isAccessActive);
  }, [brand.isAccessActive]);

  const handleSwitchChange = useCallback(
    async (isActive: boolean) => {
      setPendingActive(isActive);
      setIsUpdating(true);
      applyUserBrandAccessUpdate(brand.brandId, { isAccessActive: isActive });

      try {
        const payload = {
          brandId: brand.brandId,
          brandUserId: brand.brandUserMappingId,
          isActive,
        };
        const validated = UpdateBrandUserStatusDTO.validate(payload);
        if (!validated.success) {
          throw new Error("Invalid brand access status data");
        }

        const updatedUser = await new UpdateBrandUserStatusUseCase(
          new BrandRepository(GraphQlAdapter.getInstance().getClient()),
        ).execute(new UpdateBrandUserStatusDTO(validated.data));

        applyUserBrandAccessUpdate(brand.brandId, {
          isAccessActive: updatedUser.brandUserIsActive,
        });

        successToast({
          content: "Brand status updated",
          icon: <Icon name={Icons.CheckCircle} />,
        });
      } catch (error) {
        console.error("Failed to update brand status", error);
        setPendingActive(brand.isAccessActive);
        applyUserBrandAccessUpdate(brand.brandId, {
          isAccessActive: brand.isAccessActive,
        });
        errorToast({
          content: "Failed to update brand status. Please try again.",
          icon: <Icon name={Icons.AlertCircle} />,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [
      applyUserBrandAccessUpdate,
      brand.brandId,
      brand.brandUserMappingId,
      brand.isAccessActive,
      errorToast,
      successToast,
    ],
  );

  return (
    <HasAnyPermission requiredPermissions={[Permissions.USER_MANAGEMENT_EDIT]}>
      <Flex align="center" gap={0.5}>
        {isUpdating && <Spin size="small" spinning />}
        <Switch
          size="small"
          isSelected={pendingActive}
          onChange={handleSwitchChange}
          isDisabled={isUpdating}
          aria-label="Brand access status update"
          role="switch"
          htmlTitle="Update brand access status"
          className="ms-1"
        />
      </Flex>
    </HasAnyPermission>
  );
};
